/**
 * UI de la grabación. Tres vistas en la MISMA caja (idle / grabando / listo): sin altura estable,
 * cada transición saltaría el layout del panel entero.
 */
import React from 'react';
import {useTranslation} from 'react-i18next';

import {formatElapsed} from './feedbackAudio';
import {STATUS_ERROR, STATUS_READY, STATUS_RECORDING, STATUS_REQUESTING} from './useAudioRecorder';

export const FeedbackAudioRecorder = ({recorder, audio, onRemove, maxSeconds}) => {
    const {t} = useTranslation();

    // Sin soporte el botón NO se renderiza. Un botón deshabilitado sin explicación genera un
    // ticket de soporte; un botón ausente no promete nada.
    if (!recorder.supported) return null;

    const isRecording = recorder.status === STATUS_RECORDING;
    const isRequesting = recorder.status === STATUS_REQUESTING;

    return (
        <div className="feedback-audio">
            {audio ? (
                <div className="feedback-audio__player">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio controls src={audio.previewUrl} preload="metadata"/>
                    <span className="feedback-audio__duration">
                        {formatElapsed((audio.durationSeconds || 0) * 1000)}
                    </span>
                    <button type="button" className="feedback-audio__action" onClick={recorder.start}>
                        {t('feedback.audio.rerecord')}
                    </button>
                    <button
                        type="button"
                        className="feedback-audio__remove"
                        onClick={onRemove}
                        aria-label={t('feedback.audio.remove')}
                    >
                        ×
                    </button>
                </div>
            ) : (
                <>
                    <button
                        type="button"
                        className={`feedback-audio__record ${isRecording ? '-recording' : ''}`}
                        onClick={isRecording ? recorder.stop : recorder.start}
                        disabled={isRequesting}
                    >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            {isRecording
                                ? <rect x="7" y="7" width="10" height="10" rx="2"/>
                                : <>
                                    <rect x="9" y="3" width="6" height="11" rx="3"/>
                                    <path d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6"/>
                                </>}
                        </svg>
                        <span>
                            {isRequesting && t('feedback.audio.requesting')}
                            {isRecording && t('feedback.audio.stop')}
                            {!isRequesting && !isRecording && t('feedback.audio.record')}
                        </span>
                        {isRecording && (
                            // `role="timer"` SIN aria-live: un anuncio por segundo es tortura para
                            // un lector de pantalla. Los eventos puntuales van por la región de
                            // abajo.
                            <span
                                role="timer"
                                className={`feedback-audio__timer ${recorder.isNearLimit ? '-near' : ''}`}
                            >
                                {formatElapsed(recorder.elapsedMs)}
                            </span>
                        )}
                    </button>
                    {!isRecording && !isRequesting && (
                        <p className="feedback-audio__hint">
                            {t('feedback.audio.hint_max', {minutes: Math.round(maxSeconds / 60)})}
                        </p>
                    )}
                </>
            )}

            {/* Región de anuncios: sólo EVENTOS (empezó, quedan 10 segundos, quedó lista). */}
            <span className="feedback-visually-hidden" aria-live="polite">
                {isRecording && recorder.isNearLimit && t('feedback.audio.announce_near_limit')}
                {isRecording && !recorder.isNearLimit && t('feedback.audio.announce_recording')}
                {audio && t('feedback.audio.announce_ready', {seconds: audio.durationSeconds})}
            </span>

            {recorder.status === STATUS_ERROR && recorder.errorKey && (
                <p className="feedback-audio__error" role="alert">{t(recorder.errorKey)}</p>
            )}
            {recorder.status === STATUS_READY && audio && audio.durationSeconds >= maxSeconds && (
                <p className="feedback-audio__notice" role="status">
                    {t('feedback.audio.limit_reached', {minutes: Math.round(maxSeconds / 60)})}
                </p>
            )}
        </div>
    );
};

export default FeedbackAudioRecorder;
